import moment from 'moment'

export const TIME_FORMAT = 'HH:mm' as const
export const TIME_FORMAT_12 = 'hh:mm A' as const
export const FULL_DATE_FORMAT = 'YYYY-MM-DD' as const
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm' as const
export const FULL_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss' as const
export const FRIENDLY_DATE_FORMAT = 'ddd, D MMM' as const
export const FULL_DATE_TIME_FORMAT_WITH_T = 'YYYY-MM-DD[T]HH:mm:ss' as const
export const DATETIME_FORMAT_DAY_MONTH_TIME = 'ddd DD MMM HH:mm' as const
export const DATE_FORMAT = 'DD/MM/YYYY' as const
export const HOUR_FORMAT = 'HH' as const

const AVAILABLE_FORMATS = [
  TIME_FORMAT,
  TIME_FORMAT_12,
  FULL_DATE_FORMAT,
  DATE_TIME_FORMAT,
  FULL_DATE_TIME_FORMAT,
  FRIENDLY_DATE_FORMAT,
  FULL_DATE_TIME_FORMAT_WITH_T,
  DATE_FORMAT,
  HOUR_FORMAT
]

export interface FormattedDateProps {
  date: moment.MomentInput
  format: typeof AVAILABLE_FORMATS[number] | string
  timezoneIn?: string
  timezoneOut?: string
}

/**
 * Get the formatted date string based on format and/or timezone.
 * @param {moment.MomentInput} params.date - The date to format.
 * @param {typeof AVAILABLE_FORMATS[number]} params.format - The format string for the date.
 * @param {string} [params.timezoneIn] - Timezone of the input date. Use valid IANA timezone names, e.g., "Etc/UTC".
 * @param {string} [params.timezoneOut] - Timezone for the output date. Use valid IANA timezone names, e.g., "America/Bogota".
 * @returns {string} The formatted date string.
 * @throws {Error} If the parameters are invalid or the timezone is not recognized.
 */
export const getFormattedDate = ({ date, format, timezoneIn, timezoneOut }: FormattedDateProps): string => {
  try {
    if (!date || !format) {
      throw new Error("Both 'date' and 'format' are required parameters.")
    }

    if (timezoneIn && !moment.tz.names().includes(timezoneIn)) {
      throw new Error(`Invalid timezoneIn: ${timezoneIn}`)
    }

    if (timezoneOut && !moment.tz.names().includes(timezoneOut)) {
      throw new Error(`Invalid timezoneOut: ${timezoneOut}`)
    }

    let dateOut = timezoneIn ? moment.tz(date, timezoneIn) : moment.parseZone(date)

    if (timezoneOut) {
      dateOut = dateOut.tz(timezoneOut)
    }

    return dateOut.format(format)
  } catch (error) {
    throw new Error(`Error formatting date: ${error.message}`)
  }
}

/**
 * Get the start and end of the week (Sunday to Saturday)
 * If no `day` is passed, it uses the current date.
 * @param {string | Date | moment.Moment} [day] - The input date (optional).
 * @returns {{ from: string, to: string }} The formatted week range.
 */
export const getStartAndEndDayOfWeek = (day?: string | Date | moment.Moment) => {
  const currentDay = moment(day)

  const startOfWeek = currentDay.clone().startOf('week').day(0)
  const endOfWeek = currentDay.clone().startOf('week').day(6)

  return {
    from: startOfWeek.format(FULL_DATE_FORMAT),
    to: endOfWeek.format(FULL_DATE_FORMAT)
  }
}

/**
 * Adjusts the given date and time by rounding seconds to the next minute.
 * If the seconds are exactly 00, it will leave the time unchanged.
 * @param {string} datetime - The input date and time in "YYYY-MM-DD HH:mm:ss" format.
 * @returns {string} - The adjusted date and time in "YYYY-MM-DD HH:mm:ss" format.
 */
export const getRoundedTimeCompleted = (datetime: string): string => {
  const momentDate = moment(datetime, FULL_DATE_TIME_FORMAT)

  if (momentDate.seconds() > 0) {
    momentDate.add(1, 'minute').startOf('minute')
  }

  return momentDate.format(FULL_DATE_TIME_FORMAT)
}

export const getLocalDateAndTime = (isoString: string, timezone: string) => {
  if (isoString === '' || !isoString) {
    return { date: '', time: '' }
  }
  // Convertir el string ISO a un objeto Moment ajustado a la zona horaria
  const momentDate = moment(isoString).tz(timezone)

  // Extraer la fecha y la hora
  const date = momentDate.format('YYYY-MM-DD') // Formato de fecha: "2025-02-13"
  const time = momentDate.format('HH:mm') // Formato de hora: "19:30"

  return { date, time }
}
